import { formatTimeAgo } from "@/lib/utils/format"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { CommentResp } from "@types"
import Linkify from "react-linkify"
import { CommentProps } from "./types"
import { User } from "@/types/users"
import CommentFooter from "./CommentFooter"
import { MAX_DEPTH } from "./consts"
import ClickableAuthorHoverCard from "../ClickableAuthorHoverCard"
import SocialLinksClient from "../rail/SocialLinksClient"

/**
 * A comment with its replies.
 *
 * It calls itself recursively to render all replies.
 */
export default function Comment(props: CommentProps) {
  const { comment, depth } = props

  // Don't render comments that are more than 5 levels deep, including the parent comment.
  if (depth > MAX_DEPTH) {
    return null
  }

  const partialUser: Partial<User> = {
    id: comment.userId,
    authorName: comment.authorName,
    avatar_url: comment.avatarUrl,
  }

  return (
    <Card className="mt-4 border-none shadow-none font-body">
      <CardHeader className="pb-2.5 pl-1 pt-0 pr-0">
        <div className="flex flex-row gap-2">
          <div className="relative">
            <CommentAuthorHoverCard author={partialUser} comment={comment} />
          </div>

          <CommentAuthorAndTime comment={comment} />
        </div>
      </CardHeader>
      <CardContent className="pb-0">
        <div className="ml-7 text-muted-foreground text-sm relative">
          <p className="prose prose-sm whitespace-pre-line dark:prose-invert">
            <Linkify
              componentDecorator={(decoratedHref, decoratedText, key) => (
                <a target="blank" href={decoratedHref} key={key}>
                  {decoratedText}
                </a>
              )}
            >
              {comment.text}
            </Linkify>
          </p>
        </div>
      </CardContent>

      <CommentFooter {...props} />

      <CommentThread {...props} />
    </Card>
  )
}

/**
 * Component to handle the hover card for the user who left the comment.
 * Displays the author avatar with hover card containing bio and social links, or just the Farcaster link if it's a farcaster comment.
 */
function CommentAuthorHoverCard({
  author,
  comment,
}: {
  author: Partial<User>
  comment: CommentResp
}) {
  return (
    <ClickableAuthorHoverCard
      author={author}
      avatarClassName="h-10 w-10 border border-white dark:border-black"
      showFarcasterIcon={comment.platform === "farcaster"}
    >
      <SocialLinksClient
        className="text-muted-foreground mt-4"
        socials={
          "social" in author
            ? author.social
            : comment.paragraphSocial || {
                farcaster: comment.farcasterUsername,
              }
        }
        fcUsername={
          comment.farcasterUsername ||
          ("social" in author && author.social?.farcaster) ||
          undefined
        }
      />
    </ClickableAuthorHoverCard>
  )
}

function CommentAuthorAndTime({ comment }: { comment: CommentResp }) {
  const timeAgo = formatTimeAgo(new Date(comment.createdAt))

  return (
    <div className="flex flex-col text-sm">
      <div className="font-semibold">{comment.authorName}</div>
      <div className="text-xs text-muted-foreground">Commented {timeAgo}</div>
    </div>
  )
}

function CommentThread(props: CommentProps) {
  const { comment, depth } = props

  const hasReplies =
    comment.replies && comment.replies.length > 0 && depth < MAX_DEPTH

  if (!hasReplies || depth >= MAX_DEPTH) {
    return null
  }

  return (
    <div className="ml-10 relative">
      {comment.replies?.map((reply) => (
        <Comment key={reply.id} {...props} depth={depth + 1} comment={reply} />
      ))}
    </div>
  )
}
